import { Suspense, useEffect, useRef } from 'react'
import { Outlet, useLocation, useNavigationType } from 'react-router-dom'
import { DesktopNavShell, MobileTopBar } from '@/layouts/components/nav-shell/NavShell'
import { MobileBottomNav } from '@/layouts/components/MobileBottomNav'
import { PageChromeProvider, usePageChrome } from '@/app/providers/PageChromeProvider'
import { AmbientLayer } from '@/components/common/AmbientLayer'
import { ModuleCtaStyleSync } from '@/components/common/ModuleCtaStyleSync'
import { TaskReminderFab } from '@/components/common/TaskReminderFab'
import { MessengerFab } from '@/components/common/MessengerIcon'
import { AppUpdateBanner } from '@/components/common/AppUpdateBanner'
import { TwaUpdateBanner } from '@/components/common/TwaUpdateBanner'
import { PendingCallBanner } from '@/modules/calls/components/PendingCallBanner'
import { CallHost } from '@/modules/calls/components/CallHost'
import { useUiStore } from '@/store/ui.store'
import { useIsMobile, useIsTablet } from '@/hooks/useMediaQuery'
import { useNotificationRealtime } from '@/modules/notifications/hooks/useNotificationRealtime'
import { usePushSubscription } from '@/modules/notifications/hooks/usePushSubscription'
import { useFcmClient } from '@/modules/notifications/hooks/useFcmClient'
import { useDeniedNotificationToast } from '@/modules/notifications/hooks/useBrowserNotificationPermission'
import { primeNotificationPermissionOnGesture } from '@/modules/notifications/utils/notification-permission'
import { useAuthStore } from '@/store/auth.store'
import { CONTENT_MAX_WIDTH } from '@/constants/design-tokens'
import { ROUTES } from '@/constants/routes'
import { cn } from '@/utils/cn'
import { recordLocation } from '@/utils/nav-history'
import { prefetchRouteChunks } from '@/utils/lazy-route'
import { usePageScrollRestoration } from '@/hooks/usePageScrollRestoration'
import { RouteLoadingBar } from '@/components/common/RouteLoadingBar'

/**
 * ONE Suspense boundary that lives as long as the shell. Route navigations run
 * in a React transition, so while the next page's chunk loads the current page
 * stays on screen (thin top bar only). Per-route boundaries — plus remounting
 * the page wrapper on every pathname — used to blank the page first = blink.
 */
function RouteOutlet() {
  return (
    <Suspense fallback={<RouteLoadingBar />}>
      <Outlet />
    </Suspense>
  )
}

export function AppLayout() {
  const location = useLocation()
  const isTablet = useIsTablet()
  const isMobile = useIsMobile()
  const setSidebarCollapsed = useUiStore((s) => s.setSidebarCollapsed)
  const isAuthed = useAuthStore((s) => Boolean(s.session?.user.id))
  const navigationType = useNavigationType()
  const scrollRef = useRef<HTMLElement | null>(null)
  // Origin tracking for smart Back (idempotent per history entry).
  recordLocation(location.key, `${location.pathname}${location.search}`, navigationType)
  usePageScrollRestoration(scrollRef)

  // Messenger owns its own edge-to-edge layout and internal scrolling (split
  // inbox/thread panes) instead of the default padded/max-width/page-scrolls
  // wrapper every other route gets — computed from the URL (not page chrome
  // state, which nothing resets between routes) so it can never leak onto
  // another page. Thread-open (not the bare inbox) also hides the mobile
  // bottom nav, matching WhatsApp's own full-screen chat.
  const pathname = location.pathname
  const isMessengerRoute = pathname === ROUTES.MESSENGER || pathname.startsWith(`${ROUTES.MESSENGER}/`)
  const isMessengerThread = isMessengerRoute && pathname !== ROUTES.MESSENGER

  useNotificationRealtime()
  usePushSubscription()
  useFcmClient()
  useDeniedNotificationToast(isAuthed)

  // Chrome/Edge only show Allow/Block from a user gesture when permission is "ask".
  useEffect(() => primeNotificationPermissionOnGesture(), [])
  useEffect(() => prefetchRouteChunks(), [])

  useEffect(() => {
    if (isTablet) setSidebarCollapsed(true)
  }, [isTablet, setSidebarCollapsed])

  if (isMobile) {
    return (
      <div className="shell-canvas relative flex h-svh flex-col overflow-hidden">
        <div className="shell-ambient shell-ambient--mobile pointer-events-none absolute inset-0" aria-hidden>
          <AmbientLayer variant="quiet" />
        </div>

        <div className="relative z-10 flex h-full min-h-0 flex-col">
          <PageChromeProvider>
            <ModuleCtaStyleSync />
            <MobileTopBar />
            <TwaUpdateBanner />
            <AppUpdateBanner />
            <PendingCallBanner />

            <main
              ref={scrollRef}
              data-page-scroll
              className={cn(
                'page-stage min-h-0 flex-1 overflow-x-clip scrollbar-thin',
                isMessengerRoute
                  ? 'flex flex-col overflow-hidden'
                  : 'overflow-y-auto overscroll-y-contain px-3 pt-2 pb-3',
              )}
            >
              <AmbientLayer variant="quiet" className="ambient-layer--page-quiet" />
              <div className={cn('relative z-[1]', isMessengerRoute && 'flex min-h-0 flex-1 flex-col')}>
                <RouteOutlet />
              </div>
            </main>

            {isMessengerThread ? null : <MobileBottomNav />}
            <MessengerFab />
            <MobileTaskReminderFab />
            <CallHost />
          </PageChromeProvider>
        </div>
      </div>
    )
  }

  return (
    <div className="shell-canvas relative flex h-svh overflow-hidden">
      <div className="shell-ambient pointer-events-none absolute inset-0" aria-hidden>
        <AmbientLayer variant="shell" />
      </div>

      <div className="relative z-10 h-full w-full">
        <PageChromeProvider>
          <ModuleCtaStyleSync />
          <DesktopNavShell>
            <div className="workspace-panel relative z-[1] flex min-h-0 flex-1 flex-col overflow-hidden">
              <TwaUpdateBanner />
              <AppUpdateBanner />
              <PendingCallBanner />

              <main className="page-stage flex min-h-0 flex-1 flex-col overflow-hidden">
                <AmbientLayer variant="page" />
                <div
                  ref={(node) => {
                    scrollRef.current = node
                  }}
                  data-page-scroll
                  className={cn(
                    'relative z-[1] flex-1 overflow-x-clip scrollbar-thin',
                    isMessengerRoute ? 'flex min-h-0 flex-col overflow-hidden' : 'overflow-y-auto',
                  )}
                >
                  {isMessengerRoute ? (
                    <RouteOutlet />
                  ) : (
                    <div
                      className="mx-auto w-full px-3 pt-3 pb-5 sm:px-4 sm:pt-4 sm:pb-6 lg:px-5"
                      style={{ maxWidth: CONTENT_MAX_WIDTH }}
                    >
                      <RouteOutlet />
                    </div>
                  )}
                </div>
              </main>
            </div>
          </DesktopNavShell>
          <CallHost />
        </PageChromeProvider>
      </div>
    </div>
  )
}

/** Reads chrome.moduleId inside PageChromeProvider — fixed overlay above page UI. */
function MobileTaskReminderFab() {
  const { chrome } = usePageChrome()
  return <TaskReminderFab currentModuleId={chrome.moduleId} />
}
